<template>
  <!--
    Call-to-action button used across the marketing site.

    The accent colour is supplied through the Vuetify `primary` theme token,
    which is configured to the brand red-orange `#e7511e` in
    client/src/plugins/vuetify.js. Using the token (rather than a hardcoded hex)
    keeps the CTA colour consistent and centrally managed (Requirement 8.2).

    `v-btn` is rendered as a router link when `to` is provided, an anchor when
    `href` is provided, or a plain button otherwise. Any extra attributes
    (e.g. `block`, `disabled`, `aria-*`) fall through to the underlying button.
  -->
  <v-btn
    :color="color"
    :variant="variant"
    :size="size"
    :to="to || undefined"
    :href="href || undefined"
    :rounded="rounded"
    class="cta-button text-none"
  >
    <slot>{{ label }}</slot>
  </v-btn>
</template>

<script>
// CtaButton — brand-accent call-to-action (Requirement 8.2).
//
// The colour defaults to the `primary` theme token (brand red-orange
// `#e7511e`), so CTAs across the site share a single, centrally defined accent.
export default {
  name: 'CtaButton',
  props: {
    // Visible button text. Overridden by the default slot when provided.
    label: {
      type: String,
      default: '',
    },
    // Vuetify theme colour token. Defaults to the brand accent (`#e7511e`).
    color: {
      type: String,
      default: 'primary',
    },
    // Vuetify button variant; a solid fill makes the CTA stand out by default.
    variant: {
      type: String,
      default: 'flat',
    },
    // Vuetify button size pass-through.
    size: {
      type: String,
      default: 'large',
    },
    // Slightly rounded corners by default for a modern CTA look.
    rounded: {
      type: [Boolean, String, Number],
      default: 'lg',
    },
    // Router-link target. When set, the button navigates client-side.
    to: {
      type: [String, Object],
      default: null,
    },
    // External link target. When set, the button renders as an anchor.
    href: {
      type: String,
      default: null,
    },
  },
}
</script>

<style scoped>
.cta-button {
  font-weight: 700;
  letter-spacing: 0.02em;
}
</style>
